import { useEffect, useState } from "react";
import { getProperty } from "../api";
import { isLockedProperty, PortalCaseView, Property } from "../types";

// usePortalCase returns the Tyler court-case block for the property detail
// drawer.
//
// Load-time fix (2026-09-16): `portalCase` is the single largest field in the
// properties LIST response — 42.6% of its bytes — and the detail drawer is the
// only thing that reads it. The list endpoint is being changed to stop sending
// it, so the drawer fetches it from GET /properties/:id instead. That endpoint
// already returns the full row (and already gates portal parties by plan).
//
// When the property already carries `portalCase` (a search result, a deep link,
// or any list response from before the change) it is returned directly and no
// request is made.
//
// The fetch is best-effort and never throws into the UI: a locked teaser, a
// non-object response, or any error simply yields no court case — the same
// thing the drawer showed before for a property with no portal data. One
// request per property id per page load, including ids that have no court case.
const fetched = new Map<string, Promise<PortalCaseView | null>>();

function fetchPortalCase(id: string): Promise<PortalCaseView | null> {
  let pending = fetched.get(id);
  if (!pending) {
    pending = getProperty(id)
      .then((row) => {
        if (!row || typeof row !== "object" || Array.isArray(row)) return null;
        if (isLockedProperty(row)) return null;
        return (row as Property).portalCase ?? null;
      })
      .catch(() => null);
    fetched.set(id, pending);
  }
  return pending;
}

/** Test-only: forget previously fetched court cases. */
export function resetPortalCaseCacheForTests(): void {
  fetched.clear();
}

export function usePortalCase(property: Pick<Property, "id" | "portalCase">): PortalCaseView | undefined {
  const [loaded, setLoaded] = useState<{ id: string; portal: PortalCaseView | null } | null>(null);
  const present = property.portalCase;

  useEffect(() => {
    if (present || !property.id) return;
    let cancelled = false;
    fetchPortalCase(property.id).then((portal) => {
      // Drop the answer if the drawer has moved to another property meanwhile.
      if (!cancelled) setLoaded({ id: property.id, portal });
    });
    return () => {
      cancelled = true;
    };
  }, [property.id, present]);

  if (present) return present;
  if (loaded && loaded.id === property.id && loaded.portal) return loaded.portal;
  return undefined;
}
